import React, { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import { MobileHeader, MobileTabBar } from './components/MobileNav';
import Dashboard from './components/Dashboard';
import GridView from './components/views/GridView';
import KanbanView from './components/views/KanbanView';
import CalendarView from './components/views/CalendarView';
import GalleryView from './components/views/GalleryView';
import AnalyticsView from './components/views/AnalyticsView';
import MentionsView from './components/views/MentionsView';
import ContributionsView from './components/views/ContributionsView';
import ProspectsView from './components/views/ProspectsView';
import GuestDetailSheet from './components/GuestDetailSheet';
import EmailComposer from './components/EmailComposer';
import { ThankYouPromptDialog } from './components/ThankYouPromptDialog';
import NewGuestDialog from './components/NewGuestDialog';
import ReleaseSubmissionDialog from './components/ReleaseSubmissionDialog';
import BulkEmailDialog from './components/BulkEmailDialog';
import TemplatesEditor from './components/TemplatesEditor';
import { useApp } from './context/AppContext';
import { Mail, FileSignature, Send, Eye, Download } from 'lucide-react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Release } from './lib/api';

function TemplatesView() {
  // Editable template editor — full CRUD for the 12 email templates.
  return <TemplatesEditor />;
}

function ReleasesView({ onSelect, onCompose }) {
  const { filteredGuests, activeShowId } = useApp();
  const pending = filteredGuests.filter(g => g.releaseStatus !== 'signed');
  const [submissions, setSubmissions] = useState([]);
  const [openSubmission, setOpenSubmission] = useState(null);

  useEffect(() => {
    Release.listSubmissions(activeShowId).then(setSubmissions).catch(() => setSubmissions([]));
  }, [activeShowId, filteredGuests.length]);

  const fmt = (v) => {
    if (!v) return '—';
    try { return new Date(v).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return v; }
  };

  return (
    <div className="p-4 md:p-5 grid grid-cols-1 lg:grid-cols-2 gap-4 overflow-auto h-full">
      <Card className="border-stone-200">
        <CardHeader><CardTitle className="text-[15px] flex items-center gap-2"><FileSignature className="w-4 h-4 text-amber-600" />Pending ({pending.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {pending.map(g => (
            <div key={g.id} className="flex flex-wrap items-center justify-between gap-2 border border-stone-100 p-2.5 rounded">
              <div className="min-w-0">
                <div className="text-[13px] font-medium truncate">{g.name}</div>
                <div className="text-[11.5px] text-stone-500 truncate">{g.episodeTitle || 'No episode'}</div>
              </div>
              <div className="flex items-center gap-1.5">
                <Button size="sm" variant="outline" onClick={() => window.open(`/release/${g.id}`, '_blank')}>View form</Button>
                <Button size="sm" variant="outline" onClick={() => onCompose(g.id, 't2')}><Send className="w-3.5 h-3.5 mr-1" />Remind</Button>
                <Button size="sm" variant="ghost" onClick={() => onSelect(g.id)}>Open</Button>
              </div>
            </div>
          ))}
          {pending.length === 0 && <div className="text-stone-400 text-[13px] text-center py-6">Nothing pending.</div>}
        </CardContent>
      </Card>
      <Card className="border-stone-200">
        <CardHeader><CardTitle className="text-[15px] flex items-center gap-2"><FileSignature className="w-4 h-4 text-emerald-600" />Signed ({submissions.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {submissions.map(s => (
            <div key={s.id} className="flex items-center justify-between border border-stone-100 p-2.5 rounded hover:bg-stone-50">
              <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setOpenSubmission(s)}>
                <div className="text-[13px] font-medium truncate">{s.fullName || s.guestName}</div>
                <div className="text-[11.5px] text-stone-500 truncate">
                  <span className="font-serif italic">{s.signature}</span> • {fmt(s.submittedAt)}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" onClick={() => setOpenSubmission(s)} title="View signed form">
                  <Eye className="w-3.5 h-3.5" />
                </Button>
                <a href={Release.pdfUrl(s.id)} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="outline" title="Download PDF"><Download className="w-3.5 h-3.5" /></Button>
                </a>
                {s.guestId && <Button size="sm" variant="ghost" onClick={() => onSelect(s.guestId)}>Open</Button>}
              </div>
            </div>
          ))}
          {submissions.length === 0 && <div className="text-stone-400 text-[13px] text-center py-6">No signed releases yet.</div>}
        </CardContent>
      </Card>
      <ReleaseSubmissionDialog submission={openSubmission} open={!!openSubmission} onOpenChange={(o) => !o && setOpenSubmission(null)} />
    </div>
  );
}

export default function AppShell() {
  const { activeView, activeTable, setActiveView, activeShow } = useApp();
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [composer, setComposer] = useState({ open: false, guestId: '', templateId: '' });
  const [newOpen, setNewOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);

  // clear selection on table/show change
  useEffect(() => { setSelectedIds([]); }, [activeTable, activeShow?.id]);

  const openComposer = (guestId, templateId) => setComposer({ open: true, guestId: guestId || '', templateId: templateId || '' });

  let content;
  if (activeTable === 'templates') content = <TemplatesView />;
  else if (activeTable === 'releases') content = <ReleasesView onSelect={setSelectedGuest} onCompose={openComposer} />;
  else if (activeTable === 'analytics') content = <AnalyticsView />;
  else if (activeTable === 'mentions') content = <MentionsView />;
  else if (activeTable === 'contributions') content = <ContributionsView />;
  else if (activeTable === 'prospects') content = <ProspectsView onSelectGuest={setSelectedGuest} />;
  else if (activeView === 'dashboard') content = <Dashboard onSelect={setSelectedGuest} onCompose={() => openComposer('', '')} />;
  else if (activeView === 'grid') content = <GridView onSelect={setSelectedGuest} selectedIds={selectedIds} onSelectionChange={setSelectedIds} />;
  else if (activeView === 'kanban') content = <KanbanView onSelect={setSelectedGuest} />;
  else if (activeView === 'calendar') content = <CalendarView onSelect={setSelectedGuest} />;
  else if (activeView === 'gallery') content = <GalleryView onSelect={setSelectedGuest} />;

  // default to dashboard when entering tables that don't have views
  React.useEffect(() => {
    if (activeTable === 'guests' && !['dashboard','grid','kanban','calendar','gallery'].includes(activeView)) {
      setActiveView('dashboard');
    }
  }, [activeTable, activeView, setActiveView]);

  return (
    <div className="flex h-[100dvh] bg-[#FAF7F2]">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <MobileHeader onAdd={() => setNewOpen(true)} />
        <TopBar onAdd={() => setNewOpen(true)} />
        <div className="flex-1 overflow-hidden bg-white relative">
          {content}
          {selectedIds.length > 0 && activeTable === 'guests' && activeView === 'grid' && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-stone-900 text-white rounded-lg shadow-2xl px-3 py-2 flex items-center gap-3 z-20 animate-in fade-in slide-in-from-bottom-2 whitespace-nowrap">
              <span className="text-[12.5px] font-medium">{selectedIds.length} selected</span>
              <div className="h-4 w-px bg-stone-700" />
              <Button size="sm" onClick={() => setBulkOpen(true)} className="bg-white text-stone-900 hover:bg-stone-200 h-7 text-[12px]">
                <Send className="w-3.5 h-3.5 mr-1" /> Send email
              </Button>
              <button onClick={() => setSelectedIds([])} className="text-stone-300 hover:text-white text-[12px] px-1">Clear</button>
            </div>
          )}
        </div>
        <MobileTabBar />
      </div>

      <GuestDetailSheet
        guestId={selectedGuest}
        open={!!selectedGuest}
        onOpenChange={(o) => !o && setSelectedGuest(null)}
        onCompose={(gid, tid) => openComposer(gid, tid)}
      />
      <EmailComposer
        open={composer.open}
        onOpenChange={(o) => setComposer(c => ({ ...c, open: o }))}
        initialGuestId={composer.guestId}
        initialTemplateId={composer.templateId}
      />
      <ThankYouPromptDialog onCompose={(gid, tid) => openComposer(gid, tid)} />
      <NewGuestDialog open={newOpen} onOpenChange={setNewOpen} />
      <BulkEmailDialog open={bulkOpen} onOpenChange={setBulkOpen} guestIds={selectedIds} onSent={() => setSelectedIds([])} />
    </div>
  );
}
